import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Flame, Music, MessageSquare, UtensilsCrossed, Users, MapPin, Share2 } from 'lucide-react';

const HowItWorks = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-b from-slate-900 to-slate-800 text-white overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1548742016-14e0276fb738?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Flame className="w-16 h-16 text-amber-500 mx-auto mb-6" />
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            How It Works
          </h1>
          <p className="text-xl text-slate-300">
            Creating your Viking funeral is simple. Follow these steps to design a legendary sendoff that celebrates your life with fire, music, and glory.
          </p>
        </div>
      </section>

      {/* The Process */}
      <section className="py-24 bg-slate-50 viking-pattern-section">
        <div className="viking-pattern-bg"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h2 className="text-4xl font-bold text-center text-slate-900 mb-16">
            Your Journey to Valhalla
          </h2>

          <div className="space-y-8">
            {/* Step 1 */}
            <Card className="border-2 border-slate-200 hover:border-amber-500 transition-all duration-300">
              <CardHeader>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
                    1
                  </div>
                  <div>
                    <CardTitle className="text-2xl mb-2">Create Your Account</CardTitle>
                    <p className="text-slate-600">
                      Sign up with a simple account to save and manage your funeral plans. Your information is secure and private.
                    </p>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Step 2 */}
            <Card className="border-2 border-slate-200 hover:border-amber-500 transition-all duration-300">
              <CardHeader>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
                    2
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-2xl mb-2">Design Your Funeral</CardTitle>
                    <p className="text-slate-600 mb-4">
                      Customize every aspect of your legendary sendoff:
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="flex items-start gap-3">
                        <Music className="w-5 h-5 text-amber-600 mt-1 flex-shrink-0" />
                        <div>
                          <div className="font-semibold">Epic Soundtrack</div>
                          <p className="text-sm text-slate-600">Choose songs from Led Zeppelin to war chants</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <MessageSquare className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                        <div>
                          <div className="font-semibold">Quotes from Beyond</div>
                          <p className="text-sm text-slate-600">Share your wisdom and final words</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <UtensilsCrossed className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                        <div>
                          <div className="font-semibold">Viking Feast</div>
                          <p className="text-sm text-slate-600">Heineken, mead, and roasted meats</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Users className="w-5 h-5 text-purple-600 mt-1 flex-shrink-0" />
                        <div>
                          <div className="font-semibold">Attendance Level</div>
                          <p className="text-sm text-slate-600">Neighborhood, Tribe, or Country</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Step 3 */}
            <Card className="border-2 border-slate-200 hover:border-amber-500 transition-all duration-300">
              <CardHeader>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
                    3
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-2xl mb-2">Choose Your Vessel</CardTitle>
                    <p className="text-slate-600 mb-4">
                      Select the type of vessel for your legendary sendoff - from modern luxury to authentic Viking tradition
                    </p>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="font-semibold mb-2">🛥️ Modern Viking</div>
                        <p className="text-sm text-slate-600">Sea Ray or luxury boat - contemporary style meets tradition</p>
                      </div>
                      <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                        <div className="font-semibold mb-2">⛵ Original Viking</div>
                        <p className="text-sm text-slate-600">Wooden schooner - authentic Norse vessel experience</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="font-semibold mb-2">⚰️ Wanna Be a Viking</div>
                        <p className="text-sm text-slate-600">Standard wooden coffin - budget-friendly option</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Step 4 */}
            <Card className="border-2 border-slate-200 hover:border-amber-500 transition-all duration-300">
              <CardHeader>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
                    4
                  </div>
                  <div>
                    <CardTitle className="text-2xl mb-2">Choose Your Location</CardTitle>
                    <p className="text-slate-600 mb-4">
                      Select from sacred rivers and waterfalls across the country. We provide direct links to view locations on maps.
                    </p>
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin className="w-5 h-5 text-teal-600" />
                        <span className="font-semibold">Popular Locations:</span>
                      </div>
                      <p className="text-sm text-slate-700">Hudson River, Mississippi River, Niagara Falls, Multnomah Falls, and more</p>
                    </div>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Step 5 */}
            <Card className="border-2 border-slate-200 hover:border-amber-500 transition-all duration-300">
              <CardHeader>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
                    5
                  </div>
                  <div>
                    <CardTitle className="text-2xl mb-2">Save & Share</CardTitle>
                    <p className="text-slate-600 mb-4">
                      Once created, you'll receive a unique share code. Share your funeral plan with family and friends so they know exactly how to celebrate your life.
                    </p>
                    <div className="flex items-center gap-2 text-amber-700">
                      <Share2 className="w-5 h-5" />
                      <span className="font-semibold">Easy sharing via unique link</span>
                    </div>
                  </div>
                </div>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-white viking-pattern-section">
        <div className="viking-pattern-bg"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h2 className="text-4xl font-bold text-center text-slate-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-center text-slate-600 mb-12 text-lg">
            Everything you need to know about Viking Funeral Pyres
          </p>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border rounded-lg px-6">
              <AccordionTrigger className="text-lg font-semibold text-left">
                What is a Viking Funeral Pyre?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600">
                A Viking funeral pyre is a modern take on ancient Norse traditions. Instead of traditional somber funerals, we celebrate life with fire, music, feasting, and community - creating a memorable sendoff that honors your spirit and legacy.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border rounded-lg px-6">
              <AccordionTrigger className="text-lg font-semibold text-left">
                Is this legally allowed?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600">
                VikingFuneralPyres.com is a planning and celebration service. While actual open-air cremation is restricted in most places, we help you plan a ceremonial celebration that captures the spirit of Viking traditions within legal boundaries. We work with licensed funeral homes and venues.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border rounded-lg px-6">
              <AccordionTrigger className="text-lg font-semibold text-left">
                What's included in each attendance level?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600">
                <ul className="space-y-2">
                  <li><strong>Neighborhood (50-100):</strong> Intimate venue, basic catering, music system, and ceremony coordination</li>
                  <li><strong>Tribe (100-300):</strong> Larger venue, full Viking feast, professional sound, live musicians optional, ceremony director</li>
                  <li><strong>Country (300+):</strong> Epic venue, legendary feast, full entertainment, multiple coordinators, and custom experiences</li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border rounded-lg px-6">
              <AccordionTrigger className="text-lg font-semibold text-left">
                Why is Heineken mandatory?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600">
                In the spirit of Viking celebration and modern Nordic culture, Heineken represents quality, tradition, and festivity. It's a symbolic element that adds to the authentic celebration atmosphere. Of course, we also include mead for those seeking traditional Viking beverages!
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="border rounded-lg px-6">
              <AccordionTrigger className="text-lg font-semibold text-left">
                Can I update my funeral plan later?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600">
                Absolutely! Your funeral plan can be updated anytime. Life changes, tastes evolve, and your celebration should reflect who you are. Simply log in to your account and modify your plans as needed.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6" className="border rounded-lg px-6">
              <AccordionTrigger className="text-lg font-semibold text-left">
                How do I share my funeral plan with family?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600">
                Each funeral plan gets a unique share code and link. You can copy this link and send it via email, text, or even include it in your will. Your loved ones can view all details of your planned celebration.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-amber-600 to-orange-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Plan Your Legend?
          </h2>
          <p className="text-xl text-white/90 mb-10">
            Join thousands who have embraced a new way to celebrate life
          </p>
          <Link to="/create">
            <Button size="lg" className="bg-white text-amber-700 hover:bg-slate-100 text-lg px-10 py-6 font-semibold">
              Create Your Funeral Now
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;